import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShieldAlert, Search, Loader2, CheckCircle, AlertTriangle, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FALLBACK_SUSPICIOUS_RECEIPTS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function TravellingAdminSuspicious() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/travelling/suspicious-receipts`, { headers: getHeaders() });
      setReceipts(res.data.length > 0 ? res.data : FALLBACK_SUSPICIOUS_RECEIPTS);
    } catch { setReceipts(FALLBACK_SUSPICIOUS_RECEIPTS); toast.error("Failed to load. Showing demo data."); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const openDetail = (receipt) => {
    setSelected(receipt);
    setReviewNotes(receipt.finance_notes || "");
    setDetailOpen(true);
  };

  const handleReview = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/travelling/suspicious-receipts/${selected.id}/review`, {
        reviewed_by_finance: true,
        finance_notes: reviewNotes,
      }, { headers: getHeaders() });
      toast.success("Receipt reviewed and resolved");
      setDetailOpen(false);
      fetchReceipts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const filtered = filter === "unreviewed" ? receipts.filter((r) => !r.reviewed_by_finance) :
                  filter === "reviewed" ? receipts.filter((r) => r.reviewed_by_finance) : receipts;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">SUSPICIOUS RECEIPTS</h1>
            <p className="text-slate-600">AI-flagged suspicious receipts — finance-only access</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3">
        <Button onClick={fetchReceipts} size="sm" className="rounded-none bg-rose-600 hover:bg-rose-500 text-white">
          <Search className="w-4 h-4 mr-1" /> Refresh
        </Button>
        <div className="flex gap-1">
          {[
            { key: "all", label: "All" },
            { key: "unreviewed", label: "Unreviewed" },
            { key: "reviewed", label: "Reviewed" },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1 text-xs font-medium rounded-none ${
                filter === f.key ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-rose-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No suspicious receipts found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.id} className={`bg-white border p-4 hover:shadow-sm transition-shadow ${
              r.reviewed_by_finance ? "border-slate-200" : "border-red-200 border-l-4 border-l-red-500"
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className={`w-4 h-4 ${r.reviewed_by_finance ? "text-slate-400" : "text-red-500"}`} />
                    <span className="font-medium text-sm text-slate-800">
                      Matched: {r.matched_employee_name || "Unknown"}
                    </span>
                    {!r.reviewed_by_finance && (
                      <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 font-medium">UNREVIEWED</span>
                    )}
                    {r.reviewed_by_finance && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 font-medium">RESOLVED</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <p>Similarity Score: {(r.similarity_score * 100).toFixed(0)}%</p>
                    <p>Matched Employee ID: {r.matched_employee_id || "-"}</p>
                    <p>Flagged: {r.flagged_by_ai ? "AI" : "Manual"}</p>
                    <p className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "-"}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openDetail(r)}
                  className="rounded-none border-slate-300 ml-4">
                  <Eye className="w-4 h-4 mr-1" /> Review
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Review Suspicious Receipt</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 mt-2">
              <div className="bg-red-50 p-3 border border-red-200 text-sm space-y-1">
                <p><span className="text-slate-500">Matched Employee:</span> <strong>{selected.matched_employee_name}</strong></p>
                <p><span className="text-slate-500">Matched Employee ID:</span> {selected.matched_employee_id}</p>
                <p><span className="text-slate-500">Similarity:</span> {(selected.similarity_score * 100).toFixed(0)}%</p>
                <p><span className="text-slate-500">AI Flagged:</span> {selected.flagged_by_ai ? "✅ Yes" : "❌ No"}</p>
                <p><span className="text-slate-500">Receipt ID:</span> {selected.receipt_image_url || "N/A"}</p>
                <p><span className="text-slate-500">Matched Receipt:</span> {selected.matched_receipt_url || "N/A"}</p>
                <p><span className="text-slate-500">Status:</span> {selected.reviewed_by_finance ? "✅ Reviewed" : "🔴 Unreviewed"}</p>
              </div>

              <textarea placeholder="Finance investigation notes" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />

              {!selected.reviewed_by_finance && (
                <Button onClick={handleReview} disabled={saving} className="w-full bg-green-600 hover:bg-green-500 text-white rounded-none">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Mark as Reviewed & Resolve</>}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
